/**
 * Bridges `@coordio/ui`'s ABP-style grid contract to Cortex's own paged REST convention.
 *
 * `CordioAbpPagedDataTable` calls `dataSource(params)` on every page / sort / search / filter
 * change, where `params` is ABP-shaped:
 *
 *   { SkipCount, MaxResultCount, Sorting?, Filter?, ...typed filter keys }
 *
 * Cortex services take a different shape — `{ pageNumber, pageSize, searchTerm, ...filters }` —
 * and return `PagedResult<T>`. The two are compatible in substance: the grid only needs `items`
 * and `totalCount` back, and `PagedResult<T>` is a superset of that. What differs is the request:
 * ABP counts rows skipped, Cortex counts pages. This module is that translation, so no page has to
 * repeat it and no service has to learn ABP's vocabulary.
 *
 * WHY a shared module rather than an inline lambda per page: the offset→page arithmetic is easy to
 * get subtly wrong (an off-by-one puts the grid on page 0, which most APIs treat as page 1, so
 * "next page" appears to do nothing on the first click). Written once, it is wrong at most once.
 */

import type { AbpPagedListParams } from '@coordio/foundation';
import type { PagedResult } from '../../types/tenant';

/** The four keys ABP reserves; every other key on `AbpPagedListParams` is a column filter. */
const ABP_RESERVED_KEYS = ['SkipCount', 'MaxResultCount', 'Sorting', 'Filter'] as const;

/**
 * A Cortex list query, as the services under `src/services/` expect it.
 *
 * `searchTerm` is the toolbar's free-text search. Column filters arrive as extra keys, named by the
 * column's `queryParamKey` (falling back to its `field`) — the grid passes them through verbatim,
 * so a column declaring `queryParamKey: 'isActive'` produces `isActive` here.
 */
export interface CortexPagedQuery {
  pageNumber: number;
  pageSize: number;
  searchTerm?: string;
  /** Field name to sort by, taken from the first clause of ABP's `Sorting` string. */
  sortBy?: string;
  /** True when that first clause ends in `DESC`. */
  sortDescending?: boolean;
  [filterKey: string]: unknown;
}

/**
 * Splits ABP's `Sorting` string (`"createdAt DESC, name ASC"`) into Cortex's two fields.
 *
 * Only the first clause is used: Cortex list endpoints take a single `sortBy` + `sortDescending`
 * pair, so a second clause has nowhere to go. The grid only ever emits one clause today; if
 * multi-column sort is added later this is the place that has to grow, and dropping the extra
 * clauses silently is better than sending a string the API will reject.
 */
function parseAbpSorting(sorting: string | undefined): Pick<CortexPagedQuery, 'sortBy' | 'sortDescending'> {
  const firstClause = sorting?.split(',')[0]?.trim();
  if (!firstClause) {
    return {};
  }

  const [field, direction] = firstClause.split(/\s+/);
  if (!field) {
    return {};
  }

  return { sortBy: field, sortDescending: direction?.toUpperCase() === 'DESC' };
}

/**
 * Translates one set of grid parameters into a Cortex list query.
 *
 * Exported separately from {@link createCortexPagedDataSource} so a page that needs to reshape the
 * query further — inject a tenant id, rename a filter key the API spells differently — can do so
 * without re-deriving the page arithmetic.
 */
export function toCortexPagedQuery(params: AbpPagedListParams): CortexPagedQuery {
  // Guard the divisor: MaxResultCount is always sent by the grid, but a 0 or missing value would
  // make pageNumber Infinity/NaN and the request would fail in a way that looks like a server bug.
  const pageSize = params.MaxResultCount > 0 ? params.MaxResultCount : 25;
  const skipCount = params.SkipCount > 0 ? params.SkipCount : 0;

  const query: CortexPagedQuery = {
    pageNumber: Math.floor(skipCount / pageSize) + 1,
    pageSize,
    ...parseAbpSorting(params.Sorting),
  };

  if (params.Filter) {
    query.searchTerm = params.Filter;
  }

  for (const [key, value] of Object.entries(params)) {
    if ((ABP_RESERVED_KEYS as readonly string[]).includes(key) || value === undefined) {
      continue;
    }
    query[key] = value;
  }

  return query;
}

/**
 * Wraps a Cortex service call as a `dataSource` the grid can consume.
 *
 * @param fetchPage Calls the Cortex endpoint. Close over whatever else it needs — a tenant id, a
 *                  parent record's id — so the returned function matches the grid's signature.
 *
 * @example
 *   const dataSource = useMemo(
 *     () => createCortexPagedDataSource<ProjectDto>((query) =>
 *       projectService.getProjects(tenantId, query)),
 *     [tenantId],
 *   );
 *
 *   <CordioAbpPagedDataTable<ProjectDto> columns={columns} dataSource={dataSource} … />
 */
export function createCortexPagedDataSource<T extends { id: string }>(
  fetchPage: (query: CortexPagedQuery) => Promise<PagedResult<T>>,
): (params: AbpPagedListParams) => Promise<{ totalCount: number; items: readonly T[] }> {
  return async (params) => {
    const page = await fetchPage(toCortexPagedQuery(params));

    // The grid reads only these two fields. Returning the whole PagedResult would work but would
    // let page-level code start depending on the grid's response object, which is not a contract.
    return { totalCount: page.totalCount, items: page.items };
  };
}
